import { Component, Input, ContentChildren, QueryList, AfterContentInit, ChangeDetectorRef, HostBinding, HostListener, ElementRef, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { SidebarNavSectionComponent } from './sidebar-nav-section.component';
import { I18nService } from 'app/core/services/i18n/i18n.service';

@Component({
  selector: 'app-sidebar-nav',
  templateUrl: './sidebar-nav.component.html',
  //styleUrls: ['./sidebar-nav.component.scss']
})
export class SidebarNavComponent implements AfterContentInit, OnChanges {
  @Input() slim = false;
  @Input() ariaLabel = 'Main navigation';

  @ContentChildren(SidebarNavSectionComponent) sections!: QueryList<SidebarNavSectionComponent>;
  @ViewChild('srAnnouncement', { static: false }) srAnnouncement?: ElementRef<HTMLDivElement>;

  expandedSectionIndex: number | null = null;
  popupStyles: Map<number, { [key: string]: string }> = new Map();
  private focusedSectionIndex = 0;
  private focusedLinkIndex = -1;

  @HostBinding('class.slim-mode')
  get isSlim(): boolean {
    return this.slim;
  }

  constructor(
    private elementRef: ElementRef,
    private cd: ChangeDetectorRef,
    private i18n: I18nService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['slim'] && !changes['slim'].firstChange) {
      // Close any open popup when slim mode changes
      this.closePopup();
      
      // Update sections with new slim mode
      if (this.sections) {
        this.sections.forEach(section => {
          section.isSlim = this.slim;
        });
      }
    }
  }

  ngAfterContentInit(): void {
    // Update sections with slim mode
    this.sections.forEach(section => {
      section.isSlim = this.slim;
    });

    // Watch for changes
    this.sections.changes.subscribe(() => {
      this.sections.forEach(section => {
        section.isSlim = this.slim;
      });
      this.cd.detectChanges();
    });
  }

  toggleSection(section: SidebarNavSectionComponent, index: number, event?: MouseEvent): void {
    if (this.slim) {
      // Slim mode: Toggle popup
      if (this.expandedSectionIndex === index) {
        this.expandedSectionIndex = null;
        this.announceToScreenReader('navigation.aria.submenuCollapsed', section.label);
      } else {
        this.expandedSectionIndex = index;
        this.announceToScreenReader('navigation.aria.submenuExpanded', section.label);
        
        if (event) {
          const button = event.currentTarget as HTMLElement;
          // Initial positioning - will be refined after render
          this.popupStyles.set(index, {
            'top': `${button.getBoundingClientRect().top}px`,
            'left': `${button.getBoundingClientRect().right}px`,
            'visibility': 'hidden'
          });
          
          // Calculate proper position after popup is rendered
          this.cd.detectChanges();
          setTimeout(() => {
            this.calculatePopupPosition(button, index);
          }, 0);
        }
      }
    } else {
      // Non-slim mode: Navigate to first visible link
      const visibleLinks = section.getVisibleLinks();
      if (visibleLinks.length > 0) {
        visibleLinks[0].navigate();
      }
    }
  }

  closePopup(): void {
    if (this.expandedSectionIndex !== null) {
      const section = this.sections.toArray()[this.expandedSectionIndex];
      if (section) {
        this.announceToScreenReader('navigation.aria.submenuCollapsed', section.label);
      }
    }
    this.expandedSectionIndex = null;
  }

  private announceToScreenReader(translationKey: string, label: string): void {
    if (this.srAnnouncement) {
      const message = this.i18n.getLocalizedString(translationKey).replace('{{label}}', label);
      this.srAnnouncement.nativeElement.textContent = message;
      // Clear after announcement to allow repeat announcements
      setTimeout(() => {
        if (this.srAnnouncement) {
          this.srAnnouncement.nativeElement.textContent = '';
        }
      }, 1000);
    }
  }

  isExpanded(index: number): boolean {
    return this.expandedSectionIndex === index;
  }

  getPopupStyle(index: number): { [key: string]: string } {
    return this.popupStyles.get(index) || {};
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.slim && this.expandedSectionIndex !== null) {
      const clickedInside = this.elementRef.nativeElement.contains(event.target);
      const popup = document.querySelector('.sidebar-nav-popup');
      const clickedInPopup = popup?.contains(event.target as Node);
      
      if (!clickedInside && !clickedInPopup) {
        this.closePopup();
      }
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    
    // Handle keyboard navigation based on context
    if (target.matches('[role="menuitem"]')) {
      this.handleMenuItemKeydown(event, target);
    }
  }

  private handleMenuItemKeydown(event: KeyboardEvent, target: HTMLElement): void {
    const isSectionButton = target.tagName === 'BUTTON';
    const isLink = target.tagName === 'A';

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (this.slim) {
          // Slim mode: separate navigation for buttons and popup links
          if (isSectionButton) {
            this.focusNextSection();
          } else if (isLink) {
            this.focusNextLink(target);
          }
        } else {
          // Non-slim mode: navigate through all menu items in order
          this.focusNextMenuItem(target);
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (this.slim) {
          // Slim mode: separate navigation for buttons and popup links
          if (isSectionButton) {
            this.focusPreviousSection();
          } else if (isLink) {
            this.focusPreviousLink(target);
          }
        } else {
          // Non-slim mode: navigate through all menu items in order
          this.focusPreviousMenuItem(target);
        }
        break;

      case 'ArrowRight':
        if (isSectionButton && this.slim) {
          event.preventDefault();
          const buttonId = target.id;
          const sectionIndex = parseInt(buttonId.replace('menu-button-', ''), 10);
          if (this.expandedSectionIndex !== sectionIndex) {
            this.openSectionAndFocusFirstLink(sectionIndex);
          } else {
            this.focusFirstLinkInSection(sectionIndex);
          }
        }
        break;

      case 'ArrowLeft':
        if (isLink && this.slim && this.expandedSectionIndex !== null) {
          event.preventDefault();
          const sectionIndex = this.expandedSectionIndex;
          this.closePopup();
          // Use setTimeout to ensure focus happens after popup closes
          setTimeout(() => {
            this.focusSectionButton(sectionIndex);
          }, 0);
        }
        break;

      case 'Escape':
        if (this.slim && this.expandedSectionIndex !== null) {
          event.preventDefault();
          const sectionIndex = this.expandedSectionIndex;
          this.closePopup();
          // Use setTimeout to ensure focus happens after popup closes
          setTimeout(() => {
            this.focusSectionButton(sectionIndex);
          }, 0);
        }
        break;

      case 'Enter':
      case ' ':
        if (isSectionButton) {
          event.preventDefault();
          target.click();
        }
        // Links handle their own Enter/Space
        break;

      case 'Home':
        event.preventDefault();
        if (isSectionButton) {
          this.focusFirstSection();
        } else if (isLink) {
          this.focusFirstLinkInCurrentSection(target);
        }
        break;

      case 'End':
        event.preventDefault();
        if (isSectionButton) {
          this.focusLastSection();
        } else if (isLink) {
          this.focusLastLinkInCurrentSection(target);
        }
        break;

      case 'Tab':
        // Allow default Tab behavior but close popup in slim mode
        if (this.slim && this.expandedSectionIndex !== null) {
          this.closePopup();
        }
        break;
    }
  }

  private calculatePopupPosition(button: HTMLElement, sectionIndex?: number): void {
    const buttonRect = button.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Get actual popup element to measure its real height
    const popupElement = sectionIndex !== undefined 
      ? document.getElementById(`menu-section-${sectionIndex}`)
      : document.querySelector('.sidebar-nav-popup');
    
    const estimatedPopupWidth = 350; // matches max-width in CSS
    let popupHeight = 400; // default fallback
    
    // Use actual popup height if available
    if (popupElement instanceof HTMLElement) {
      popupHeight = popupElement.offsetHeight || popupElement.scrollHeight;
    }
    
    // 1. Default position: top-right aligned to triggering button
    let top = buttonRect.top;
    let left = buttonRect.right;
    let maxHeight: string | undefined;
    
    // 2. Check if popup overflows bottom of viewport
    const bottomOverflow = (top + popupHeight) - viewportHeight;
    
    if (bottomOverflow > 0) {
      // Move popup up to fit in viewport
      top = top - bottomOverflow - 10; // 10px padding from bottom
      
      // 3. Check if moving it up causes top overflow
      if (top < 0) {
        // Place at top of viewport and limit height
        top = 10; // 10px padding from top
        maxHeight = `${viewportHeight - 20}px`; // Full viewport height minus padding
      }
    }
    
    // Always ensure it doesn't overflow the viewport bottom
    const availableHeight = viewportHeight - top - 10; // 10px padding
    if (!maxHeight || parseInt(maxHeight) > availableHeight) {
      maxHeight = `${availableHeight}px`;
    }
    
    // Ensure popup doesn't overflow right side of viewport
    if (left + estimatedPopupWidth > viewportWidth) {
      left = viewportWidth - estimatedPopupWidth - 10; // 10px padding from right
    }
    
    // Ensure popup doesn't go off-screen to the left
    if (left < 0) {
      left = 10; // 10px padding from left
    }
    
    this.popupStyles.set(sectionIndex!, {
      'top': `${top}px`,
      'left': `${left}px`,
      'max-height': maxHeight,
      'overflow-y': 'auto',
      'visibility': 'visible'
    });
    
    this.cd.detectChanges();
  }

  private focusNextSection(): void {
    const buttons = this.getSectionButtons();
    const currentIndex = Array.from(buttons).findIndex(btn => btn === document.activeElement);
    const nextIndex = (currentIndex + 1) % buttons.length;
    (buttons[nextIndex] as HTMLElement).focus();
  }

  private focusPreviousSection(): void {
    const buttons = this.getSectionButtons();
    const currentIndex = Array.from(buttons).findIndex(btn => btn === document.activeElement);
    const prevIndex = currentIndex <= 0 ? buttons.length - 1 : currentIndex - 1;
    (buttons[prevIndex] as HTMLElement).focus();
  }

  private focusFirstSection(): void {
    const buttons = this.getSectionButtons();
    if (buttons.length > 0) {
      (buttons[0] as HTMLElement).focus();
    }
  }

  private focusLastSection(): void {
    const buttons = this.getSectionButtons();
    if (buttons.length > 0) {
      (buttons[buttons.length - 1] as HTMLElement).focus();
    }
  }

  private focusNextMenuItem(currentElement: HTMLElement): void {
    // Get all focusable menu items (buttons and links) in non-slim mode
    const allItems = this.getAllMenuItems();
    const currentIndex = Array.from(allItems).indexOf(currentElement);
    
    if (currentIndex < allItems.length - 1) {
      (allItems[currentIndex + 1] as HTMLElement).focus();
    } else {
      // Wrap to first item
      (allItems[0] as HTMLElement).focus();
    }
  }

  private focusPreviousMenuItem(currentElement: HTMLElement): void {
    // Get all focusable menu items (buttons and links) in non-slim mode
    const allItems = this.getAllMenuItems();
    const currentIndex = Array.from(allItems).indexOf(currentElement);
    
    if (currentIndex > 0) {
      (allItems[currentIndex - 1] as HTMLElement).focus();
    } else {
      // Wrap to last item
      (allItems[allItems.length - 1] as HTMLElement).focus();
    }
  }

  private focusNextLink(currentLink: HTMLElement): void {
    if (this.slim) {
      // In slim mode, only cycle within current popup
      const links = this.getLinksInCurrentContext(currentLink);
      const currentIndex = Array.from(links).indexOf(currentLink);
      if (currentIndex < links.length - 1) {
        (links[currentIndex + 1] as HTMLElement).focus();
      }
    } else {
      // In non-slim mode, cycle through all links across all sections
      const allLinks = this.getAllLinks();
      const currentIndex = Array.from(allLinks).indexOf(currentLink);
      if (currentIndex < allLinks.length - 1) {
        (allLinks[currentIndex + 1] as HTMLElement).focus();
      }
    }
  }

  private focusPreviousLink(currentLink: HTMLElement): void {
    if (this.slim) {
      // In slim mode, go to section button when at first link
      const links = this.getLinksInCurrentContext(currentLink);
      const currentIndex = Array.from(links).indexOf(currentLink);
      if (currentIndex > 0) {
        (links[currentIndex - 1] as HTMLElement).focus();
      } else {
        // Focus section button when going up from first link
        const section = currentLink.closest('li[role="none"]');
        if (section) {
          const button = section.querySelector('button[role="menuitem"]');
          if (button) {
            (button as HTMLElement).focus();
          }
        }
      }
    } else {
      // In non-slim mode, cycle through all links across all sections
      const allLinks = this.getAllLinks();
      const currentIndex = Array.from(allLinks).indexOf(currentLink);
      if (currentIndex > 0) {
        (allLinks[currentIndex - 1] as HTMLElement).focus();
      } else {
        // Go to the parent section button
        const parentSection = currentLink.closest('ul[role="menu"]')?.closest('li[role="none"]');
        if (parentSection) {
          const button = parentSection.querySelector('button[role="menuitem"]');
          if (button) {
            (button as HTMLElement).focus();
          }
        }
      }
    }
  }

  private focusFirstLinkInCurrentSection(currentLink: HTMLElement): void {
    const links = this.getLinksInCurrentContext(currentLink);
    if (links.length > 0) {
      (links[0] as HTMLElement).focus();
    }
  }

  private focusLastLinkInCurrentSection(currentLink: HTMLElement): void {
    const links = this.getLinksInCurrentContext(currentLink);
    if (links.length > 0) {
      (links[links.length - 1] as HTMLElement).focus();
    }
  }

  private openSectionAndFocusFirstLink(sectionIndex: number): void {
    // Open the section
    const sectionsArray = this.sections.toArray();
    if (sectionIndex < sectionsArray.length) {
      this.expandedSectionIndex = sectionIndex;
      
      // Update popup position using two-phase rendering
      const button = document.getElementById(`menu-button-${sectionIndex}`);
      if (button) {
        // Phase 1: Render hidden for measurement
        const buttonRect = button.getBoundingClientRect();
        this.popupStyles.set(sectionIndex, {
          'top': `${buttonRect.top}px`,
          'left': `${buttonRect.right}px`,
          'visibility': 'hidden'
        });
        this.cd.detectChanges();
        
        // Phase 2: Measure and position
        setTimeout(() => {
          this.calculatePopupPosition(button, sectionIndex);
          // Focus first link after popup is positioned
          setTimeout(() => {
            this.focusFirstLinkInSection(sectionIndex);
          }, 0);
        }, 0);
      }
    }
  }

  private focusFirstLinkInSection(sectionIndex: number): void {
    const menuSection = document.getElementById(`menu-section-${sectionIndex}`);
    if (menuSection) {
      const firstLink = menuSection.querySelector('a[role="menuitem"]');
      if (firstLink) {
        (firstLink as HTMLElement).focus();
      }
    }
  }

  private focusSectionButton(sectionIndex: number): void {
    const button = document.getElementById(`menu-button-${sectionIndex}`);
    if (button) {
      button.focus();
    }
  }

  private getSectionButtons(): NodeListOf<Element> {
    return this.elementRef.nativeElement.querySelectorAll('button[role="menuitem"]');
  }

  private getAllMenuItems(): NodeListOf<Element> {
    // Get all menu items (buttons and links) in document order for non-slim mode
    // This excludes items in popups (which only exist in slim mode)
    return this.elementRef.nativeElement.querySelectorAll('button[role="menuitem"], ul[role="menu"]:not(.sidebar-nav-popup ul) a[role="menuitem"]');
  }

  private getAllLinks(): NodeListOf<Element> {
    // Get all links across all sections (excluding popup in slim mode)
    return this.elementRef.nativeElement.querySelectorAll('ul[role="menu"]:not(.sidebar-nav-popup ul) a[role="menuitem"]');
  }

  private getLinksInCurrentContext(currentLink: HTMLElement): NodeListOf<Element> {
    // Find if we're in a popup or inline list
    const popup = currentLink.closest('.sidebar-nav-popup');
    const inlineList = currentLink.closest('ul[role="menu"]');
    
    if (popup) {
      return popup.querySelectorAll('a[role="menuitem"]');
    } else if (inlineList) {
      return inlineList.querySelectorAll('a[role="menuitem"]');
    }
    
    return this.elementRef.nativeElement.querySelectorAll('a[role="menuitem"]');
  }
}
